import Segmented from "@/components/Segmented";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Gavel, Users2, Clock3, Trash2 } from "lucide-react";
import NewEventDialog, { EventTypeBadge } from "@/components/NewEventDialog";
import ExportMenu from "@/components/ExportMenu";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import LastUpdatedLabel from "@/components/LastUpdatedLabel";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

import ProcessoLink from "@/components/ProcessoLink";
import useEstadoSessao from "@/hooks/useEstadoSessao";
import CompromissoCard from "@/components/CompromissoCard";
// Ícone e cor por tipo de evento — mesma lógica de cores do badge (EventTypeBadge, Lote
// 56): audiência em dourado escuro (marca) com ícone preto, reunião em azul, prazo em
// cinza mais escuro com ícone preto.
const EVENT_ICON = {
  audiencia: { icon: Gavel, iconClass: "text-black", boxClass: "bg-emerald-brand" },
  reuniao: { icon: Users2, iconClass: "text-blue-700", boxClass: "bg-blue-100" },
  prazo: { icon: Clock3, iconClass: "text-black", boxClass: "bg-stone-300" },
};

const WEEKDAY_LABELS = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];
const WEEKDAY_LABELS_LONG = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MONTH_LABELS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const MONTH_LABELS_SHORT = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

// Grade de horário das visões Dia e Semana — 5h às 23h ("até 00h"). Lote 54 original só
// cobria 6h-21h nas duas; Lote 60 estendeu só a visão Dia pra 5h-23h a pedido do Robson;
// agora (lote novo, 02/10/2026) ele pediu pra igualar a Semana à Dia — mesma faixa nas
// duas visões. A rolagem vertical da grade (max-h-[65vh], ver HourGrid) já cobre a faixa
// maior sem problema.
const HOURS = Array.from({ length: 19 }, (_, i) => i + 5); // 5..23 (até 00h)

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayISO = () => toISO(new Date());
const hourLabel = (h) => `${pad(h)}:00`;

// Segunda-feira como primeiro dia da semana — mesma convenção já usada no card
// "Essa semana" do Painel.
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // 0 = segunda
  const gridStart = new Date(year, month, 1 - startOffset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

function buildWeekDays(anchorISO) {
  const anchor = new Date(anchorISO + "T00:00:00");
  const offset = (anchor.getDay() + 6) % 7; // 0 = segunda
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() - offset);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

// Um evento "pertence" à hora do grid mais próxima abaixo do horário real (ex: 14:15 cai
// na linha das 14h) — evita ter que desenhar posições proporcionais em pixel.
const eventHour = (h) => {
  const t = h.hearing?.time || "";
  const hh = parseInt(t.slice(0, 2), 10);
  return Number.isNaN(hh) ? null : hh;
};

// Célula de evento reutilizada nas visões Dia/Semana — compacta, com link pro detalhe.
// O texto quebra em vez de cortar (Lote 54): numa coluna estreita (visão Semana em tela
// pequena) a linha da grade cresce pra caber, em vez de truncar o nome das partes.
const EventChip = ({ h }) => {
  const { iconClass, boxClass } = EVENT_ICON[h.hearing?.event_type] || EVENT_ICON.audiencia;
  return (
    <Link
      to={`/audiencia/${h.pub_id}`}
      data-testid={`calendar-item-${h.pub_id}`}
      className={`block rounded px-1.5 py-1 text-[11px] leading-tight mb-1 last:mb-0 ${boxClass} ${iconClass} hover:opacity-80`}
      title={h.hearing?.parties || h.hearing?.description || h.hearing?.court || ""}
    >
      <span className="font-bold tabular-nums">{h.hearing?.time || "—"}</span>{" "}
      <span className="break-words">
        {h.hearing?.parties || h.hearing?.description || h.hearing?.court || "—"}
      </span>
    </Link>
  );
};

// Lote 77 — visão Semana no celular: cada coluna tem ~43px, então o texto completo não cabe.
// Em vez dele, cada horário mostra um bloco colorido pequeno (hora + primeira palavra) ou
// "+N" quando há vários eventos na mesma hora. Tocar no bloco abre um cartão com o texto
// completo (CompactSlot → detalhe). No computador a grade continua igual.
const useIsMobile = () => {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => { try { return window.matchMedia(q).matches; } catch { return false; } });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(q); } catch { return undefined; }
    const on = (e) => setM(e.matches);
    setM(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else mq.removeListener(on); };
  }, []);
  return m;
};

const eventTitle = (h) => h.hearing?.parties || h.hearing?.description || h.hearing?.court || "—";
const firstWord = (t) => {
  const w = (t || "").trim().split(/\s+/)[0] || "—";
  return w.length > 6 ? `${w.slice(0, 5)}.` : w;
};

const CompactSlot = ({ items, onOpen }) => {
  const first = items[0];
  const { iconClass, boxClass } = EVENT_ICON[first.hearing?.event_type] || EVENT_ICON.audiencia;
  return (
    <button
      type="button"
      onClick={() => onOpen(items)}
      data-testid={`calendar-compact-${first.pub_id}`}
      aria-label={`${items.length} evento(s) às ${first.hearing?.time || ""}: ${eventTitle(first)}`}
      className={`block w-full rounded px-0.5 py-1 text-[9px] leading-[1.1] text-center overflow-hidden active:opacity-70 ${boxClass} ${iconClass}`}
    >
      <span className="block font-bold tabular-nums">{(first.hearing?.time || "—").slice(0, 5)}</span>
      {items.length > 1 ? (
        <span className="block font-extrabold">+{items.length}</span>
      ) : (
        <span className="block truncate">{firstWord(eventTitle(first))}</span>
      )}
    </button>
  );
};

// Cartão (folha inferior) com o texto completo dos eventos do horário tocado.
const EventDetailSheet = ({ items, onClose }) => (
  <Sheet open={!!items} onOpenChange={(o) => { if (!o) onClose(); }}>
    <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl" data-testid="calendar-event-sheet">
      <SheetHeader className="text-left">
        <SheetTitle>
          {items?.[0] ? `${fmtDayLabel(items[0].hearing?.date)}${items.length > 1 ? ` · ${items.length} eventos` : ""}` : ""}
        </SheetTitle>
        <SheetDescription className="sr-only">Detalhes dos eventos do horário selecionado</SheetDescription>
      </SheetHeader>
      <div className="mt-3 space-y-3">
        {(items || []).map((h) => (
          <div key={h.pub_id} className="rounded-lg border border-stone-200 p-3 text-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold tabular-nums">{h.hearing?.time || "—"}</span>
              <EventTypeBadge hearing={h.hearing} />
            </div>
            <p className="font-medium text-stone-900 break-words">{eventTitle(h)}</p>
            {h.hearing?.court && <p className="text-stone-600 break-words">{h.hearing.court}</p>}
            {h.hearing?.case_number && <p className="text-stone-500 text-xs tabular-nums break-all">Proc. <ProcessoLink numero={h.hearing.case_number} /></p>}
            <Link
              to={`/audiencia/${h.pub_id}`}
              data-testid={`calendar-sheet-open-${h.pub_id}`}
              className="inline-block mt-2 text-sm font-semibold text-emerald-brand underline"
            >
              Abrir detalhes
            </Link>
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);

const fmtDayLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return `${WEEKDAY_LABELS_LONG[(d.getDay() + 6) % 7]}, ${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
};

// Semana "expandida" no celular: lista dia a dia com o texto completo (botão Expandir/Resumir).
const WeekListExpanded = ({ cols, byDate }) => (
  <div className="border border-stone-200 rounded-lg divide-y divide-stone-200" data-testid="week-expanded">
    {cols.map((d) => {
      const iso = toISO(d);
      const list = (byDate[iso] || []).slice().sort((a, b) => (a.hearing?.time || "").localeCompare(b.hearing?.time || ""));
      return (
        <div key={iso} className={`p-3 ${iso === todayISO() ? "bg-gold-100" : ""}`}>
          <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5">{fmtDayLabel(iso)}</p>
          {list.length === 0 ? (
            <p className="text-xs text-stone-400">Sem eventos</p>
          ) : list.map((h) => <EventChip key={h.pub_id} h={h} />)}
        </div>
      );
    })}
  </div>
);

// Botão "+" que abre "Novo evento" já com data/hora daquele horário. Lote 54: no toque
// (celular), só abre com toque-e-segurar (~500ms) — um toque rápido não abre nada, pra
// não criar evento sem querer ao rolar a tela. No mouse (computador), continua um clique
// normal, sem mudança. Implementado sem estado próprio de "aberto": o toque-e-segurar
// dispara um `.click()` programático no próprio gatilho do diálogo (Radix DialogTrigger),
// e um toque curto suprime o clique sintético do navegador via preventDefault no touchend
// (por isso não dá pra usar só onClick aqui — teria que distinguir toque de mouse, coisa
// que o evento "click" não informa).
const AddSlotButton = ({ iso, time, onCreated, className, children }) => {
  const triggerRef = useRef(null);
  const timerRef = useRef(null);
  const movedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const onTouchStart = () => {
    movedRef.current = false;
    clearTimer();
    timerRef.current = setTimeout(() => {
      triggerRef.current?.click();
    }, 500);
  };
  const onTouchMove = () => {
    movedRef.current = true;
    clearTimer();
  };
  const onTouchEnd = (e) => {
    clearTimer();
    if (!movedRef.current) e.preventDefault();
  };

  return (
    <NewEventDialog
      onCreated={onCreated}
      initialDate={iso}
      initialTime={time}
      trigger={
        // Lote novo (02/10/2026, pedido do Robson): sem o ícone de "+" fixo em cada
        // horário vazio — a legenda abaixo da grade (SlotHintFooter) já explica que
        // basta clicar/tocar e segurar. O botão continua ocupando a célula inteira
        // (clicável), só ganha um realce suave no hover pra indicar que é interativo.
        <button
          ref={triggerRef}
          type="button"
          data-testid={`calendar-add-slot-${iso}-${time}`}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={clearTimer}
          className={className || "w-full h-full hover:bg-stone-100/70 rounded transition-colors"}
        >
          {children}
        </button>
      }
    />
  );
};

// Grade de horas compartilhada pelas visões Dia e Semana — `cols` é a lista de dias
// (1 pra Dia, 7 pra Semana) exibidos lado a lado, e `hours` é o intervalo de horário
// exibido (a mesma faixa nas duas visões desde o lote novo de 02/10/2026, ver HOURS
// acima). Lote 54: cabeçalho
// dos dias fica fixo no topo (sticky) enquanto só o corpo da grade rola verticalmente,
// dentro de uma altura máxima — assim os 7 dias da Semana continuam todos visíveis lado a
// lado (as colunas só ficam mais estreitas, o texto do evento quebra em linha em vez de
// cortar) sem precisar de rolagem horizontal, e sem perder de vista qual dia é qual ao
// rolar pra ver as horas mais tarde/cedo.
// Lote novo (02/10/2026, pedido do Robson, só visão Semana): linha clara separando cada
// dia e coluna inteira de sábado/domingo em cinza claro — calculado por coluna e
// aplicado tanto no cabeçalho quanto em cada célula de hora dessa coluna.
// Lote novo (02/10/2026, pedido do Robson): na visão Dia (uma coluna só), o próprio
// rótulo da hora à esquerda ("14:00") agora também agenda — faz mais sentido clicar
// exatamente onde o horário está escrito do que num espaço vazio ao lado, sem nenhuma
// referência visual. Passar o mouse por cima (ou tocar, no celular) deixa o texto em
// negrito, indicando que é clicável. Na Semana (7 colunas) o rótulo é compartilhado por
// 7 dias diferentes — não dá pra saber em qual deles agendar só de clicar na hora — então
// ali o rótulo continua só texto, sem ação.
const HourGrid = ({ cols, hours, byDate, onCreated, compact = false, onOpenSlot }) => {
  const singleIso = cols.length === 1 ? toISO(cols[0]) : null;
  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <div className="max-h-[65vh] overflow-y-auto">
        <div className="grid" style={{ gridTemplateColumns: `48px repeat(${cols.length}, 1fr)` }}>
          <div className="sticky top-0 z-10 border-b border-r border-stone-300 bg-stone-50" />
          {cols.map((d, idx) => {
            const iso = toISO(d);
            const isToday = iso === todayISO();
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            const isLast = idx === cols.length - 1;
            return (
              <div
                key={iso}
                className={`sticky top-0 z-10 border-b text-center py-2 ${isLast ? "" : "border-r"} border-stone-300 ${
                  isToday ? "bg-gold-100" : isWeekend ? "bg-stone-100" : "bg-stone-50"
                }`}
              >
                <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-stone-400">
                  {WEEKDAY_LABELS_LONG[(d.getDay() + 6) % 7]}
                </div>
                <div className={`text-sm font-bold tabular-nums ${isToday ? "text-emerald-brand" : "text-stone-900"}`}>
                  {d.getDate()}
                </div>
              </div>
            );
          })}
          {hours.map((hour) => {
            const singleEmpty = singleIso
              ? (byDate[singleIso] || []).filter((h) => eventHour(h) === hour).length === 0
              : false;
            return (
              <Fragment key={hour}>
                <div className="border-r border-b border-stone-300 pt-1">
                  {singleEmpty ? (
                    <AddSlotButton
                      iso={singleIso}
                      time={hourLabel(hour)}
                      onCreated={onCreated}
                      className="w-full h-full flex items-center justify-end pr-1 sm:pr-1.5 text-[9px] sm:text-[10px] tabular-nums text-stone-400 hover:text-stone-900 hover:font-extrabold active:text-stone-900 active:font-extrabold rounded transition-colors"
                    >
                      {hourLabel(hour)}
                    </AddSlotButton>
                  ) : (
                    <span className="block text-[9px] sm:text-[10px] text-stone-400 text-right pr-1 sm:pr-1.5 tabular-nums">
                      {hourLabel(hour)}
                    </span>
                  )}
                </div>
                {cols.map((d, idx) => {
                  const iso = toISO(d);
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const isLast = idx === cols.length - 1;
                  const items = (byDate[iso] || []).filter((h) => eventHour(h) === hour);
                  return (
                    <div
                      key={`${iso}-${hour}`}
                      className={`border-b border-stone-100 min-h-[2.5rem] p-0.5 relative group ${isLast ? "" : "border-r border-stone-300"} ${
                        isWeekend ? "bg-stone-50" : ""
                      }`}
                    >
                      {items.length > 0 ? (
                        compact ? (
                          <CompactSlot items={items} onOpen={onOpenSlot} />
                        ) : (
                          <div>
                            {items.map((h) => <EventChip key={h.pub_id} h={h} />)}
                          </div>
                        )
                      ) : (
                        <div className="h-full">
                          <AddSlotButton iso={iso} time={hourLabel(hour)} onCreated={onCreated} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Legenda discreta explicando o gesto de criar evento pelo horário vazio — só aparece
// nas visões Dia/Semana, onde esse gatilho existe (Lote 54).
const SlotHintFooter = () => (
  <p className="text-[11px] text-stone-400 mt-2 text-center">
    Dica: toque e segure um horário vazio pra criar um evento nele (no computador, um clique já abre).
  </p>
);

const VIEWS = [
  { key: "dia", label: "Dia" },
  { key: "semana", label: "Semana" },
  { key: "mes", label: "Mês" },
  { key: "ano", label: "Ano" },
];

export default function CalendarPage() {
  const [view, setView] = useEstadoSessao("calendario_visao", "mes", { validos: ["mes", "semana", "dia", "ano"] });   // Lote 131: Voltar mantém a visão
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selected, setSelected] = useState(todayISO());
  const [items, setItems] = useState([]);
  const [toDelete, setToDelete] = useState(null);
  const isMobile = useIsMobile();
  const [weekExpanded, setWeekExpanded] = useState(false);
  const [slotItems, setSlotItems] = useState(null);

  // Lote 96: /calendario?data=AAAA-MM-DD abre direto naquele dia (letreiro: toque no conflito).
  const [searchParams, setSearchParams] = useSearchParams();
  // Lote 131: /calendario?data=...&conflito=idA,idB mostra, no alto do dia, os compromissos em conflito (com partes).
  const [conflitoIds, setConflitoIds] = useState(() => (searchParams.get("conflito") || "").split(",").map((x) => x.trim()).filter(Boolean).slice(0, 6));
  useEffect(() => {
    const d = searchParams.get("data");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m] = d.split("-").map(Number);
      setSelected(d);
      setCursor({ year: y, month: m - 1 });
      setView("dia");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => http.get("/publications", { params: { status: "approved" } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  // Lote 57: atualiza sozinho ao voltar pra tela e a cada 15min enquanto fica aberta.
  const { lastUpdated } = useAutoRefresh(load);

  const excluirEvento = async () => {
    if (!toDelete) return;
    try {
      await http.post(`/publications/${toDelete}/cancel`, {});
      toast.success("Evento excluído");
      setItems((cur) => cur.filter((i) => i.pub_id !== toDelete));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    } finally {
      setToDelete(null);
    }
  };

  const byDate = useMemo(() => {
    const map = {};
    for (const item of items) {
      const date = item.hearing?.date;
      if (!date) continue;
      if (!map[date]) map[date] = [];
      map[date].push(item);
    }
    return map;
  }, [items]);

  const days = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);
  const weekDays = useMemo(() => buildWeekDays(selected), [selected]);
  const today = todayISO();
  const selectedItems = (byDate[selected] || []).slice().sort((a, b) => (a.hearing?.time || "").localeCompare(b.hearing?.time || ""));

  // Navegação — o que "anterior/próximo/hoje" fazem muda conforme a visão ativa, igual
  // ao Calendário da Apple (no Dia navega 1 dia, na Semana 7 dias, no Mês 1 mês, no Ano
  // 1 ano). Selecionar um dia numa visão mantém esse dia ao trocar pra outra visão.
  const goToday = () => {
    const now = new Date();
    setCursor({ year: now.getFullYear(), month: now.getMonth() });
    setSelected(todayISO());
  };
  const shiftSelected = (deltaDays) => {
    const d = new Date(selected + "T00:00:00");
    d.setDate(d.getDate() + deltaDays);
    const iso = toISO(d);
    setSelected(iso);
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
  };
  const prevMonth = () => setCursor((c) => (c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 }));
  const nextMonth = () => setCursor((c) => (c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 }));
  const prevYear = () => setCursor((c) => ({ ...c, year: c.year - 1 }));
  const nextYear = () => setCursor((c) => ({ ...c, year: c.year + 1 }));

  const goPrev = () => {
    if (view === "dia") shiftSelected(-1);
    else if (view === "semana") shiftSelected(-7);
    else if (view === "ano") prevYear();
    else prevMonth();
  };
  const goNext = () => {
    if (view === "dia") shiftSelected(1);
    else if (view === "semana") shiftSelected(7);
    else if (view === "ano") nextYear();
    else nextMonth();
  };

  const headerLabel = () => {
    if (view === "ano") return `${cursor.year}`;
    if (view === "dia") {
      return new Date(selected + "T00:00:00").toLocaleDateString("pt-BR", {
        weekday: "long", day: "2-digit", month: "long",
      });
    }
    if (view === "semana") {
      const first = weekDays[0], last = weekDays[6];
      const sameMonth = first.getMonth() === last.getMonth();
      return sameMonth
        ? `${first.getDate()}–${last.getDate()} de ${MONTH_LABELS[first.getMonth()]} ${first.getFullYear()}`
        : `${first.getDate()} ${MONTH_LABELS_SHORT[first.getMonth()]} – ${last.getDate()} ${MONTH_LABELS_SHORT[last.getMonth()]} ${last.getFullYear()}`;
    }
    return `${MONTH_LABELS[cursor.month]} ${cursor.year}`;
  };

  const openDayFromYear = (iso, d) => {
    setSelected(iso);
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
    setView("dia");
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto pb-24 md:pb-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-stone-900">Calendário</h1>
          <p className="text-sm text-stone-500 mt-0.5">Audiências e compromissos por dia</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            <ExportMenu />
            <NewEventDialog onCreated={load} />
          </div>
          <LastUpdatedLabel date={lastUpdated} />
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <Segmented tone="light" fit size="sm" value={view} onChange={setView} testid="calendar-views"
          options={VIEWS.map((v) => ({ key: v.key, label: v.label, testid: `calendar-view-${v.key}` }))} />
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={goPrev} data-testid="calendar-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-display font-bold text-stone-900 text-sm md:text-base tabular-nums capitalize min-w-[9rem] text-center">
            {headerLabel()}
          </div>
          <Button variant="ghost" size="icon" onClick={goNext} data-testid="calendar-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="calendar-today" className="ml-1">
            Hoje
          </Button>
        </div>
      </div>

      {view === "mes" && (
        <div className="bg-white border border-stone-200 rounded-lg p-4 md:p-5">
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAY_LABELS.map((w) => (
              <div key={w} className="text-center text-[10px] font-bold uppercase tracking-widest text-stone-400 py-1">
                {w}
              </div>
            ))}
          </div>
          {/* Lote novo (02/10/2026, pedido do Robson): células com altura fixa menor em
              vez de aspect-square — num monitor largo, o quadrado (altura = largura da
              coluna) ficava grande demais e empurrava a lista de eventos do dia (abaixo)
              fora da tela sem rolar. Mantém espaço suficiente pro número + bolinha. */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const iso = toISO(d);
              const inMonth = d.getMonth() === cursor.month;
              const isToday = iso === today;
              const isSelected = iso === selected;
              const count = (byDate[iso] || []).length;
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => setSelected(iso)}
                  data-testid={`calendar-day-${iso}`}
                  className={`h-10 sm:h-12 rounded-md flex flex-col items-center justify-center gap-0.5 text-sm transition-colors
                    ${!inMonth ? "text-stone-300" : "text-stone-700"}
                    ${isSelected ? "bg-stone-900 text-white" : isToday ? "bg-gold-500/15 text-emerald-brand font-bold" : "hover:bg-stone-100"}
                  `}
                >
                  <span className="tabular-nums">{d.getDate()}</span>
                  {count > 0 && (
                    <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-emerald-brand"}`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === "semana" && (
        <>
          {isMobile && (
            <div className="flex justify-end mb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setWeekExpanded((v) => !v)}
                data-testid="week-toggle-expand"
              >
                {weekExpanded ? "Resumir" : "Expandir"}
              </Button>
            </div>
          )}
          {isMobile && weekExpanded ? (
            <WeekListExpanded cols={weekDays} byDate={byDate} />
          ) : (
            <>
              <HourGrid
                cols={weekDays} hours={HOURS} byDate={byDate} onCreated={load}
                compact={isMobile} onOpenSlot={setSlotItems}
              />
              <SlotHintFooter />
            </>
          )}
          <EventDetailSheet items={slotItems} onClose={() => setSlotItems(null)} />
        </>
      )}
      {view === "dia" && conflitoIds.length > 0 && items.some((i) => conflitoIds.includes(i.pub_id)) && (
        <div className="mb-4 border border-red-300 bg-red-50 rounded-lg p-3 space-y-2" data-testid="calendario-conflito">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-red-700 flex-1">Conflito de horário</span>
            <button type="button" data-testid="calendario-conflito-fechar" onClick={() => setConflitoIds([])}
              className="text-xs text-red-700 underline-offset-2 hover:underline min-h-[32px] px-1">Fechar</button>
          </div>
          {items.filter((i) => conflitoIds.includes(i.pub_id)).sort((a, b) => (a.hearing?.time || "").localeCompare(b.hearing?.time || "")).map((h) => (
            <CompromissoCard key={h.pub_id} h={h} isToday={h.hearing?.date === today} />
          ))}
        </div>
      )}
      {view === "dia" && (
        <>
          <HourGrid cols={[new Date(selected + "T00:00:00")]} hours={HOURS} byDate={byDate} onCreated={load} />
          <SlotHintFooter />
        </>
      )}

      {view === "ano" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {MONTH_LABELS_SHORT.map((label, month) => {
            const miniDays = buildMonthGrid(cursor.year, month);
            return (
              <button
                key={month}
                type="button"
                data-testid={`calendar-year-month-${month}`}
                onClick={() => { setCursor({ year: cursor.year, month }); setView("mes"); }}
                className="bg-white border border-stone-200 rounded-lg p-2.5 text-left hover:border-stone-400 transition-colors"
              >
                <div className="text-xs font-bold text-stone-900 mb-1.5">{label}</div>
                <div className="grid grid-cols-7 gap-[2px]">
                  {miniDays.map((d) => {
                    const iso = toISO(d);
                    const inMonth = d.getMonth() === month;
                    const dayItems = byDate[iso] || [];
                    const hasAudiencia = dayItems.some((h) => h.hearing?.event_type === "audiencia");
                    const has = dayItems.length > 0;
                    const isToday = iso === today;
                    return (
                      <div
                        key={iso}
                        onClick={(e) => { e.stopPropagation(); if (inMonth) openDayFromYear(iso, d); }}
                        className={`relative aspect-square rounded-sm flex items-center justify-center text-[8px] tabular-nums
                          ${!inMonth ? "text-stone-200" :
                            isToday ? "bg-stone-900 text-white font-bold" :
                            hasAudiencia ? "bg-emerald-brand font-bold" :
                            has ? "bg-stone-100 text-stone-700" : "text-stone-400"}
                        `}
                      >
                        {inMonth ? d.getDate() : ""}
                        {isToday && hasAudiencia && (
                          <span className="absolute bottom-[1px] w-1 h-1 rounded-full bg-emerald-brand" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {(view === "mes") && (
        <div className="mt-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">
            {selected === today ? "Hoje" : new Date(selected + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
          </h2>
          {selectedItems.length === 0 ? (
            <div className="text-sm text-stone-400 bg-white border border-dashed border-stone-200 rounded-lg py-10 text-center">
              Nada agendado para este dia.
            </div>
          ) : (
            <div className="space-y-2">
              {selectedItems.map((h) => {
                const { iconClass, boxClass } = EVENT_ICON[h.hearing?.event_type] || EVENT_ICON.audiencia;
                return (
                  <div
                    key={h.pub_id}
                    data-testid={`calendar-item-${h.pub_id}`}
                    className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg p-3 hover:border-stone-400 transition-all"
                  >
                    <Link to={`/audiencia/${h.pub_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`min-w-[3rem] h-9 px-1.5 rounded-md flex items-center justify-center shrink-0 ${boxClass}`}>
                        <span className={`text-xs font-bold tabular-nums ${iconClass}`}>
                          {h.hearing?.time || "—"}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-0.5">
                          <EventTypeBadge hearing={h.hearing} />
                        </div>
                        <div className="text-sm text-stone-700 truncate">
                          {h.hearing?.parties || h.hearing?.description || h.hearing?.court || "—"}
                        </div>
                      </div>
                    </Link>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-stone-400 hover:text-rose-700 shrink-0"
                      onClick={() => setToDelete(h.pub_id)}
                      data-testid={`calendar-delete-${h.pub_id}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este evento?</AlertDialogTitle>
            <AlertDialogDescription>
              O evento sai do seu Google Calendar. O registro fica marcado como cancelado e
              continua consultável na Agenda — pode ser reativado de lá se precisar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={excluirEvento} className="bg-rose-600 hover:bg-rose-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
